/**
 * formatSupabaseError — turn an unknown caught value into a string suitable
 * for an alert() / toast / inline error.
 *
 * Why this exists: Supabase's PostgrestError (returned in `{error}` from
 * supabase-js queries) is a *plain object* with `message`/`details`/`hint`/
 * `code` fields — it is NOT `instanceof Error`. The naive
 *   `err instanceof Error ? err.message : String(err)`
 * pattern (which was used in TransitionButton, FinanceDashboard, and
 * AdminControlsPanel) hits the `String(err)` branch for every Supabase
 * failure and produces "[object Object]", swallowing the real diagnostic.
 *
 * The May 6 2026 supplement-invoice save bug (column did not exist) got
 * masked entirely by this. Fix: pull `.message` (and the other PG fields when
 * useful) out of the plain-object shape too.
 *
 * Returns a single user-friendly string. Pass the result to alert() / toast.
 */
// Friendly copy reused across the app when a request never reaches the server
// (content blocker, captive portal, VPN/proxy, Private Relay, dropped
// connection). Mirrors NETWORK_INTERCEPT_HINT in edgeError.ts.
const NETWORK_INTERCEPT_MESSAGE =
  "Couldn't reach the server — the request was blocked before it left your browser. " +
  "Try disabling ad/privacy blockers for this site (or use a private window), " +
  "toggle Wi-Fi off/on, or switch networks, then try again.";

/**
 * Detect the bare network-failure throws that mean the request never landed:
 *   - supabase-js: "Failed to send a request to the Edge Function" (FunctionsFetchError)
 *   - Chrome/Firefox: "Failed to fetch" / "NetworkError when attempting to fetch resource."
 *   - iOS Safari: "Load failed"
 *   - aborts/timeouts: AbortError / TimeoutError
 * These all surface to the user as cryptic strings; we want the actionable hint instead.
 */
function isNetworkFailureValue(err: unknown): boolean {
  if (!err) return false;
  const name = (err as { name?: string }).name;
  if (name === "FunctionsFetchError" || name === "AbortError" || name === "TimeoutError") return true;
  if (err instanceof TypeError) return true;
  const msg = (err as { message?: string }).message ?? "";
  return /failed to send a request to the edge function|load failed|failed to fetch|networkerror|network request failed/i.test(
    msg,
  );
}

export function formatSupabaseError(err: unknown): string {
  if (err == null) return "Unknown error";

  // Request never reached the server (blocker / proxy / dropped connection).
  // Surface the actionable hint instead of the raw "Failed to send a request…".
  if (isNetworkFailureValue(err)) return NETWORK_INTERCEPT_MESSAGE;

  // Native Error / DOMException / fetch errors
  if (err instanceof Error) return err.message || String(err);

  if (typeof err === "string") return err;

  if (typeof err === "object") {
    const obj = err as Record<string, unknown>;
    const parts: string[] = [];

    if (typeof obj.message === "string" && obj.message) {
      parts.push(obj.message);
    }
    // PostgrestError: details often has the schema-level diagnostic
    // ("column 'x' of relation 'y' does not exist", FK violation hint, etc.)
    if (typeof obj.details === "string" && obj.details && obj.details !== obj.message) {
      parts.push(obj.details);
    }
    // hint is usually a "did you mean ..." suggestion
    if (typeof obj.hint === "string" && obj.hint) {
      parts.push(`Hint: ${obj.hint}`);
    }
    // code (e.g. "42703" for undefined column) is useful for debugging
    if (typeof obj.code === "string" && obj.code) {
      parts.push(`(${obj.code})`);
    }

    if (parts.length > 0) return parts.join(" — ");

    // Last resort — try JSON. Better than "[object Object]" because it shows
    // *something* rather than nothing.
    try {
      const json = JSON.stringify(err);
      if (json && json !== "{}") return json;
    } catch {
      // fall through
    }
  }

  return String(err);
}
